'use client'

import { useMemo } from 'react'

import { computeXStackLayout } from '../lib/x-layout-engine'
import { type GapKind, type XPreviewProfile, webTextBelowProfile, xPreviewProfiles } from '../lib/x-layout-profile'

export interface XPreviewProps {
  imageUrls: string[]
  slices: { width: number; height: number }[]
  profileName: XPreviewProfile['name']
  gapKind: GapKind
  viewportWidth: number
  onGapKindChange: (kind: GapKind) => void
  onViewportWidthChange: (width: number) => void
}

const pickProfile = (name: string): XPreviewProfile => {
  const found = xPreviewProfiles.find((profile) => profile.name === name)
  return found ?? webTextBelowProfile
}

export const XPreview = ({
  imageUrls,
  slices,
  profileName,
  gapKind,
  viewportWidth,
  onGapKindChange,
  onViewportWidthChange
}: XPreviewProps) => {
  const profile = useMemo(() => pickProfile(profileName), [profileName])

  const resolvedViewportWidth = useMemo(() => {
    const rule = profile.containerWidthRule
    return rule.kind === 'fixed' ? rule.width : Math.round(rule.max)
  }, [profile])

  const sliderRange = useMemo(() => {
    const rule = profile.containerWidthRule
    return rule.kind === 'fixed'
      ? { min: rule.width, max: rule.width }
      : { min: rule.min, max: rule.max }
  }, [profile])

  const layout = useMemo(
    () =>
      computeXStackLayout(
        slices.map((slice) => ({ width: slice.width, height: slice.height })),
        profile,
        { gapKind, viewportWidth: viewportWidth || resolvedViewportWidth }
      ),
    [gapKind, profile, resolvedViewportWidth, slices, viewportWidth]
  )

  const paired = useMemo(() => {
    const count = Math.min(imageUrls.length, layout.items.length)
    return Array.from({ length: count }, (_, index) => ({
      url: imageUrls[index],
      item: layout.items[index]
    }))
  }, [imageUrls, layout.items])

  const hasClampWarning = layout.items.some((item) => item.isMaxClamped)
  const previewWidthLabel =
    profile.containerWidthRule.kind === 'responsive'
      ? `viewport=${String(viewportWidth || resolvedViewportWidth)}px → Wc=${String(layout.containerWidth)}px`
      : `Wc=${String(layout.containerWidth)}px`

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-amber-200/60 bg-gradient-to-b from-amber-400/10 to-amber-400/5 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg font-semibold text-amber-100">ポストシミュレーター</h3>
        <span className="rounded-full bg-amber-300 px-3 py-1 text-xs font-bold text-slate-950">縦並び再現</span>
      </div>

      <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-slate-950/50 p-3 text-sm text-amber-50">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold">余白</span>
          <div className="flex gap-2">
            {(['small', 'large'] as GapKind[]).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => {
                  onGapKindChange(kind)
                }}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  gapKind === kind
                    ? 'border-amber-300 bg-amber-300 text-slate-950'
                    : 'border-amber-200/50 bg-transparent text-amber-100 hover:border-amber-200'
                }`}
              >
                {kind} ({profile.gapCssPx[kind]}px)
              </button>
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1">
          <span className="flex items-center justify-between text-xs text-amber-100">
            <span>表示幅 (viewport)</span>
            <span>{previewWidthLabel}</span>
          </span>
          <input
            type="range"
            min={sliderRange.min}
            max={sliderRange.max}
            step={1}
            value={profile.containerWidthRule.kind === 'fixed' ? profile.containerWidthRule.width : viewportWidth}
            onChange={(event) => {
              const next = Number(event.target.value)
              onViewportWidthChange(next)
            }}
            disabled={profile.containerWidthRule.kind === 'fixed'}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-amber-300 disabled:opacity-60"
          />
        </label>
      </div>

      {hasClampWarning ? (
        <div className="rounded-xl border border-amber-300/40 bg-amber-300/15 p-3 text-xs text-amber-50">
          5:1を超える画像があるため、横幅がトリミングされています。X上ではGapはlarge (48px)になります。
        </div>
      ) : null}

      <div
        className="relative mx-auto w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-black/70 shadow-inner"
        style={{ width: '100%', maxWidth: layout.containerWidth }}
        data-testid="x-preview-frame"
      >
        <div className="absolute right-3 top-3 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold text-amber-100">
          object-fit: cover / gap {String(layout.usedGap)}px
        </div>
        <div className="flex flex-col">
          {paired.map((pair, index) => (
            <div
              key={pair.url}
              className="overflow-hidden border-b border-white/5 last:border-b-0"
              data-testid={`x-preview-item-${String(index + 1)}`}
              style={{
                aspectRatio: `${String(layout.containerWidth)} / ${String(pair.item.displayHeight)}`,
                marginBottom: index === paired.length - 1 ? 0 : layout.usedGap
              }}
            >
              <img
                src={pair.url}
                alt={`Xプレビュー ${String(index + 1)}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
